// SubscriptionPage: Full-page subscription management
// Transform from modal to dedicated page for better mobile UX

import React, { useState } from 'react'
import { useSubscription } from '@/hooks/useSubscription'
import { useCredits } from '@/hooks/useCredits'
import { CreditBalance } from '../billing/CreditBalance'
import { userProfileClientRepair } from '@/services/UserProfileClientRepair'
import {
  CreditCard,
  Crown,
  Activity,
  Calendar,
  Receipt,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  Settings
} from 'lucide-react'

interface SubscriptionPageProps {
  onNavigateToPricing?: () => void
}

export function SubscriptionPage({ onNavigateToPricing }: SubscriptionPageProps) {
  const {
    subscription,
    isLoading,
    error,
    isSubscribed,
    isTrial,
    isPaidSubscriber,
    isPremium,
    openCustomerPortal,
    canAccessFeature
  } = useSubscription()

  // Development mode detection
  const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'

  const { balance, isLoading: creditsLoading } = useCredits()
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [isRepairing, setIsRepairing] = useState(false)
  const [repairMessage, setRepairMessage] = useState<string | null>(null)
  const [showDebugInfo, setShowDebugInfo] = useState(false)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [isLoadingDebug, setIsLoadingDebug] = useState(false)

  const handleManageBilling = async () => {
    try {
      setIsOpeningPortal(true)
      setPortalError(null)

      // Check if user has active paid subscription for billing portal
      if (!canAccessFeature('billing_portal')) {
        if (isTrial) {
          setPortalError('Billing portal is available for paid subscribers. You\'re currently on a free trial - upgrade to a paid plan to access billing management.')
        } else {
          setPortalError('Billing portal access requires an active paid subscription. Please upgrade or reactivate your subscription.')
        }
        return
      }

      // Attempt to open the Stripe customer portal
      await openCustomerPortal(window.location.href)

    } catch (error) {
      console.error('Failed to open customer portal:', error)

      // Handle specific error types from the enhanced BillingService
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase()

        // Handle trial user blocked error
        if (errorMessage.includes('trial') && errorMessage.includes('upgrade')) {
          setPortalError('You are currently on a free trial. Upgrade to a paid plan to access billing management and invoices.')
          return
        }

        // Handle billing setup incomplete error
        if (errorMessage.includes('billing account') && errorMessage.includes('setup')) {
          setPortalError('Your billing account needs to be set up. This sometimes happens with manually created accounts - please contact support for assistance.')
          return
        }

        // Handle general access denied
        if (errorMessage.includes('access denied') || errorMessage.includes('permission')) {
          setPortalError('Access denied. Please ensure you have an active paid subscription to access the billing portal.')
          return
        }

        // Default error handling
        setPortalError(`Unable to access billing portal: ${error.message}. Please contact support if this issue persists.`)
      } else {
        setPortalError('Unable to access billing portal. Please contact support if this issue persists.')
      }
    } finally {
      setIsOpeningPortal(false)
    }
  }

  const handleRepairProfile = async () => {
    setIsRepairing(true)
    setRepairMessage(null)

    try {
      const result = await userProfileClientRepair.repairUserProfile()

      if (!result.success) {
        setRepairMessage(`❌ Repair failed: ${result.message}`)
      } else if (result.status === 'exists') {
        setRepairMessage(`✅ Profile exists - billing should work now!`)
      } else if (result.status === 'created') {
        setRepairMessage(`🎉 Profile created successfully - try billing again!`)
        // Refresh the subscription data
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    } catch (error) {
      setRepairMessage(`❌ Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsRepairing(false)
    }
  }

  const handleLoadDebugInfo = async () => {
    setIsLoadingDebug(true)
    try {
      const info = await userProfileClientRepair.getProfileDebugInfo()
      setDebugInfo(info)
    } catch (error) {
      console.error('Error loading debug info:', error)
    } finally {
      setIsLoadingDebug(false)
    }
  }

  const getPlanBadgeColor = (planName?: string) => {
    switch (planName?.toLowerCase()) {
      case 'business':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'professional':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'starter':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getUsagePercentage = () => {
    if (!balance) return 0
    const used = balance.used_this_month || 0
    const total = subscription?.credits_per_month || 1
    return Math.min((used / total) * 100, 100)
  }

  if (isLoading || creditsLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-24 bg-gray-200 rounded-lg"></div>
          </div>
        ))}
      </div>
    )
  }

  // Note: Removed early error return to ensure users always see the full interface
  // with repair tools available. Errors are now displayed within the main UI.

  return (
    <div className="space-y-8">
      {/* Development Mode Banner */}
      {isDevelopment && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center">
          <Crown className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-purple-800 font-medium">Local Development Mode</p>
            <p className="text-purple-600 text-sm mt-1">
              Running locally with connection to deployed Edge Functions. All features should work normally.
            </p>
          </div>
        </div>
      )}

      {/* Subscription Loading Error */}
      {error && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-orange-800 font-medium">Subscription Data Loading Issue</p>
            <p className="text-orange-600 text-sm mt-1">{error}</p>
            <p className="text-orange-600 text-sm mt-1">
              Don't worry - this often happens with test accounts. Use the repair tools below to fix this automatically.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="ml-auto bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Portal Error/Info Message */}
      {portalError && (
        <div className={`border rounded-lg p-4 flex items-center ${
          portalError.includes('development mode') || portalError.includes('Edge Functions')
            ? 'bg-purple-50 border-purple-200'
            : isTrial
            ? 'bg-blue-50 border-blue-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <AlertCircle className={`w-5 h-5 mr-3 flex-shrink-0 ${
            portalError.includes('development mode') || portalError.includes('Edge Functions')
              ? 'text-purple-600'
              : isTrial
              ? 'text-blue-600'
              : 'text-red-600'
          }`} />
          <div className="flex-1">
            <p className={`font-medium ${
              portalError.includes('development mode') || portalError.includes('Edge Functions')
                ? 'text-purple-800'
                : isTrial
                ? 'text-blue-800'
                : 'text-red-800'
            }`}>
              {portalError.includes('development mode') || portalError.includes('Edge Functions')
                ? 'Development Mode - Limited Functionality'
                : isTrial
                ? 'Trial Account - Upgrade Available'
                : 'Billing Portal Access Issue'
              }
            </p>
            <p className={`text-sm mt-1 ${
              portalError.includes('development mode') || portalError.includes('Edge Functions')
                ? 'text-purple-600'
                : isTrial
                ? 'text-blue-600'
                : 'text-red-600'
            }`}>
              {portalError}
            </p>
            {(portalError.includes('Edge Function not found') || portalError.includes('404')) && (
              <p className="text-purple-600 text-sm mt-2">
                💡 Edge Function deployment issue detected. Please check Supabase deployment status.
              </p>
            )}
            {portalError.includes('Authentication required') && (
              <p className="text-blue-600 text-sm mt-2">
                💡 Please try refreshing the page and logging in again.
              </p>
            )}
            {isTrial && !portalError.includes('development mode') && (
              <div className="mt-3">
                <button
                  onClick={onNavigateToPricing}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  View Upgrade Options
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setPortalError(null)}
            className={`ml-auto hover:opacity-80 text-xl leading-none ${
              portalError.includes('development mode') || portalError.includes('Edge Functions')
                ? 'text-purple-600'
                : isTrial
                ? 'text-blue-600'
                : 'text-red-600'
            }`}
          >
            ×
          </button>
        </div>
      )}

      {/* Debug and Repair Section */}
      {(error || portalError) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Settings className="w-5 h-5 text-yellow-600 mr-3" />
              <div>
                <h4 className="text-yellow-800 font-medium">Development Tools</h4>
                <p className="text-yellow-700 text-sm">Diagnostic tools to fix billing issues</p>
              </div>
            </div>
            <button
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
            >
              {showDebugInfo ? 'Hide' : 'Show'} Debug Info
            </button>
          </div>

          {showDebugInfo && (
            <div className="border-t border-yellow-200 pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={handleRepairProfile}
                  disabled={isRepairing}
                  className="flex items-center justify-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isRepairing ? 'animate-spin' : ''}`} />
                  <span>{isRepairing ? 'Repairing...' : 'Repair Profile'}</span>
                </button>

                <button
                  onClick={handleLoadDebugInfo}
                  disabled={isLoadingDebug}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Settings className={`w-4 h-4 ${isLoadingDebug ? 'animate-spin' : ''}`} />
                  <span>{isLoadingDebug ? 'Loading...' : 'Load Debug Info'}</span>
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="flex items-center justify-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Reload Page</span>
                </button>
              </div>

              {repairMessage && (
                <div className="bg-white border border-yellow-200 rounded p-3">
                  <p className="text-sm font-mono">{repairMessage}</p>
                </div>
              )}

              {debugInfo && (
                <div className="bg-white border border-yellow-200 rounded p-3">
                  <h5 className="font-medium text-yellow-800 mb-2">Profile Debug Info:</h5>
                  <pre className="text-xs text-yellow-700 whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              )}

              <div className="bg-white border border-yellow-200 rounded p-3">
                <h5 className="font-medium text-yellow-800 mb-2">Current Status:</h5>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Subscription Error: {error || 'None'}</li>
                  <li>• Portal Error: {portalError || 'None'}</li>
                  <li>• Credits Loading: {creditsLoading ? 'Yes' : 'No'}</li>
                  <li>• Subscription Loading: {isLoading ? 'Yes' : 'No'}</li>
                  <li>• Status: {subscription?.status || 'Unknown'}</li>
                  <li>• Is Trial: {isTrial ? 'Yes' : 'No'}</li>
                  <li>• Is Paid Subscriber: {isPaidSubscriber ? 'Yes' : 'No'}</li>
                  <li>• Is Subscribed (Any): {isSubscribed ? 'Yes' : 'No'}</li>
                  <li>• Can Access Billing Portal: {canAccessFeature('billing_portal') ? 'Yes' : 'No'}</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Current Plan Overview */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Crown className="w-7 h-7 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Current Plan</h2>
              <p className="text-gray-600">Your subscription details</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-semibold border ${getPlanBadgeColor(subscription?.plan_name)}`}>
            {subscription?.plan_name || (isTrial ? 'Free Trial' : 'No Plan')}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-3">
              <Activity className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-gray-900">Credits Available</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">
              {balance?.balance?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-gray-600">
              {subscription?.credits_per_month?.toLocaleString() || '0'} per month
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-3">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">Usage This Month</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-2">
              {getUsagePercentage().toFixed(0)}%
            </p>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${getUsagePercentage()}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-3">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-gray-900">Status</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1 capitalize">
              {subscription?.status || 'Unknown'}
            </p>
            <p className="text-sm text-gray-600">
              {isPaidSubscriber
                ? 'Paid subscription active'
                : isTrial
                ? 'Free trial active'
                : 'No active subscription'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Credit Balance Details */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Credit Balance Details</h3>
          <CreditBalance />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleManageBilling}
            disabled={isOpeningPortal}
            className={`flex items-center justify-center space-x-3 px-8 py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium ${
              canAccessFeature('billing_portal')
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
            }`}
            title={
              canAccessFeature('billing_portal')
                ? "Open Stripe billing portal"
                : isTrial
                ? "Upgrade to paid plan for billing access"
                : "Requires active subscription"
            }
          >
            <CreditCard className="w-5 h-5" />
            <span>{isOpeningPortal ? 'Opening Portal...' : 'Manage Billing'}</span>
            {canAccessFeature('billing_portal') && <ExternalLink className="w-4 h-4" />}
          </button>

          {(
            <button
              onClick={onNavigateToPricing}
              className="flex items-center justify-center space-x-3 bg-green-600 text-white px-8 py-4 rounded-xl hover:bg-green-700 transition-colors shadow-sm"
            >
              <Crown className="w-5 h-5" />
              <span className="font-medium">
                {isPaidSubscriber ? 'Change Plan' : 'Upgrade Plan'}
              </span>
            </button>
          )}
        </div>

        {/* Billing Portal Information */}
        <div className={`rounded-xl p-6 border ${
          isPaidSubscriber
            ? 'bg-gray-50 border-gray-200'
            : isTrial
            ? 'bg-blue-50 border-blue-200'
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start space-x-4">
            <Receipt className={`w-6 h-6 mt-1 ${
              isPaidSubscriber
                ? 'text-gray-600'
                : isTrial
                ? 'text-blue-600'
                : 'text-yellow-600'
            }`} />
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {isPaidSubscriber
                  ? 'Billing Portal Access'
                  : isTrial
                  ? 'Trial Account Status'
                  : 'Account Status'
                }
              </h4>
              <p className="text-gray-700 leading-relaxed">
                {isPaidSubscriber
                  ? 'Access your complete billing history, update payment methods, download invoices, and manage your subscription through our secure Stripe portal.'
                  : isTrial
                  ? 'You\'re currently on a free trial with 100 credits. Upgrade to a paid plan to access the billing portal, get more credits, and unlock premium features.'
                  : 'The billing portal is available for users with active paid subscriptions. Upgrade to a paid plan to access comprehensive billing management features.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Plan Comparison Teaser */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {isPaidSubscriber ? 'Explore Other Plans' : 'Unlock Premium Features'}
              </h4>
              <p className="text-gray-700">
                {isPaidSubscriber
                  ? 'Compare plans and find the perfect fit for your workflow.'
                  : 'Get more credits, priority support, and advanced features with a paid plan.'
                }
              </p>
            </div>
            <button
              onClick={onNavigateToPricing}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium whitespace-nowrap"
            >
              View All Plans
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPage